import React from "react";
import { useTranslation } from "react-i18next";
import "./ContactTopics.css";

/**
 * Page Contact — Section 02 : Choix du besoin.
 * Une seule source de vérité, `selectedSubject`, partagée avec le <select>
 * existant dans Contact.jsx. Pas de modification de sendContactEmail.
 */
const CONTENT = {
  fr: {
    eyebrow: "01 / COMPRENDRE VOTRE BESOIN",
    title: "Que souhaitez-vous faire ?",
    intro: "Sélectionnez le sujet de votre demande pour mieux orienter votre échange.",
    choiceLabel: "Choix du sujet de votre demande",
    items: [
      {
        value: "demo",
        icon: "fleet",
        title: "Découvrir OPENlift",
        description: "Supervision de parc, diagnostic et démonstration."
      },
      {
        value: "devis",
        icon: "iss",
        title: "Devis Téléalarme ISS",
        description: "Étudier vos besoins de communication d'urgence 4G/IP."
      },
      {
        value: "partenariat",
        icon: "partner",
        title: "Devenir partenaire",
        description: "Échanger sur une collaboration commerciale ou technique."
      },
      {
        value: "autre",
        icon: "other",
        title: "Autre demande",
        description: "Poser une question à l'équipe UniTech."
      }
    ]
  },
  en: {
    eyebrow: "01 / UNDERSTANDING YOUR NEEDS",
    title: "How can we help you?",
    intro: "Select the subject of your enquiry so we can direct you to the right person.",
    choiceLabel: "Choose the subject of your enquiry",
    items: [
      {
        value: "demo",
        icon: "fleet",
        title: "Discover OPENlift",
        description: "Fleet monitoring, diagnostics and a demonstration."
      },
      {
        value: "devis",
        icon: "iss",
        title: "ISS emergency call quote",
        description: "Discuss your 4G/IP emergency communication needs."
      },
      {
        value: "partenariat",
        icon: "partner",
        title: "Become a partner",
        description: "Explore commercial or technical cooperation."
      },
      {
        value: "autre",
        icon: "other",
        title: "Other enquiry",
        description: "Ask the UniTech team a question."
      }
    ]
  }
};

function TopicIcon({ kind }) {
  const shared = {
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: "false"
  };
  if (kind === "fleet") {
    return (
      <svg {...shared}>
        <rect x="4.5" y="3" width="15" height="18" rx="3" />
        <rect x="8" y="6.5" width="8" height="9" rx="1.2" />
        <path d="M10.5 18h3" />
        <path d="M7 10.5h1M16 10.5h1" />
      </svg>
    );
  }
  if (kind === "iss") {
    return (
      <svg {...shared}>
        <path d="M8 9.5c2.3-2.3 5.7-2.3 8 0" />
        <path d="M5 6.5c3.9-3.9 10.1-3.9 14 0" />
        <path d="M10.5 13a2.2 2.2 0 0 1 3 0" />
        <circle cx="12" cy="17.2" r="1.2" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (kind === "partner") {
    return (
      <svg {...shared}>
        <path d="m3 8 4-3 4.5 2.5M21 8l-4-3-4.5 2.5" />
        <path d="M8 11.3 10.3 9a2.2 2.2 0 0 1 3.2 0l3.5 3.6a2 2 0 0 1-2.7 2.9l-3.7-3" />
        <path d="M6 11 4 13l6.7 6a2 2 0 0 0 2.6-.1l1.3-1.2M3 8v5M21 8v5" />
      </svg>
    );
  }
  return (
    <svg {...shared}>
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v9a2.5 2.5 0 0 1-2.5 2.5H9l-5 4V5.5Z" />
      <path d="M8 8.5h8M8 12h5" />
    </svg>
  );
}

export default function ContactTopics({ selectedSubject = "", onSubjectChange }) {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const copy = CONTENT[language] || CONTENT.fr;

  return (
    <section id="contact-besoin" className="contact-topics" aria-labelledby="contact-topics-title">
      <div className="contact-topics__container">
        <header className="contact-topics__header">
          <p className="contact-topics__eyebrow">{copy.eyebrow}</p>
          <h2 className="contact-topics__title" id="contact-topics-title">{copy.title}</h2>
          <p className="contact-topics__intro">{copy.intro}</p>
        </header>
        <div className="contact-topics__grid" role="group" aria-label={copy.choiceLabel}>
          {copy.items.map((item) => {
            const active = selectedSubject === item.value;
            return (
              <button
                type="button"
                key={item.value}
                className={`contact-topics__card${active ? " contact-topics__card--active" : ""}`}
                aria-pressed={active}
                onClick={() => onSubjectChange(item.value)}
              >
                <span className="contact-topics__icon"><TopicIcon kind={item.icon} /></span>
                {active && (
                  <span className="contact-topics__check" aria-hidden="true">
                    <svg viewBox="0 0 20 20" focusable="false"><path d="m4.5 10 3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                )}
                <span className="contact-topics__name">{item.title}</span>
                <span className="contact-topics__description">{item.description}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
